/** @file node.add: adds a node under an existing parent, keeping parent-first key order. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { PuppetNode } from '@zilo/pose3d-format/types/puppet';
import { expectShape } from '@zilo/pose3d-format/validate/fields/expectShape';
import { NODE_SHAPE } from '@zilo/pose3d-format/validate/shapes/nodeShape';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import { readInput } from '../validate/readInput';
import { orderNodes } from './orderNodes';

/** `node.add { name, node }` → `{ name }`: `node.parent` must exist, `node.mirror` is ignored; inverse: remove it. */
export const nodeAddCommand: CommandDef<'node.add'> = {
  name: 'node.add',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['name', 'node']);
    return { name: r.name('name'), node: expectShape(r.raw('node'), r.at('node'), NODE_SHAPE) as PuppetNode };
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const p = draft.puppet;
    if (Object.hasOwn(p.nodes, input.name)) throwError(ERROR_CODES.VALIDATION, { node: input.name }, { path: '/input/name', hint: 'the node exists' });
    const node = structuredClone(input.node);
    delete node.mirror;
    if (node.parent === undefined) {
      throwError(ERROR_CODES.VALIDATION, { node: input.name }, { path: '/input/node/parent', hint: 'the puppet has a root; name a parent' });
    }
    if (!Object.hasOwn(p.nodes, node.parent)) {
      throwError(ERROR_CODES.NODE_PARENT_UNKNOWN, { node: input.name, parent: node.parent }, { path: '/input/node/parent' });
    }
    p.nodes = orderNodes({ ...p.nodes, [input.name]: node });
    checkPuppet(draft, ctx);
    return { result: { name: input.name }, warnings: [], inverse: [{ name: 'node.remove', input: { name: input.name, children: 'remove' } }] };
  },
};
